import React from "react";
import { createTheme, MantineColorsTuple, MantineTheme } from "@mantine/core";

/**
 * Terminal palette: neutral slate accent, theme-invariant buy/sell semantics.
 * Raw surface tokens (--pulse-*) live in ./tokens.css.
 */

// Encodes both schemes' text/accent steps: [1]=dark fg, [2]=dark primary,
// [5]=light muted-fg, [6]=dark muted-fg, [8]=light primary, [9]=light fg.
const slate: MantineColorsTuple = [
  "#eef1f6",
  "#dce4ef",
  "#cdd5e0",
  "#aebccd",
  "#8fa3ba",
  "#7a90a8",
  "#5a7090",
  "#42546e",
  "#2d3f55",
  "#1a2332",
];

const success: MantineColorsTuple = [
  "#ecfdf5",
  "#d1fae5",
  "#a7f3d0",
  "#6ee7b9",
  "#34d399", // Buy / positive
  "#10b981",
  "#059669",
  "#047857",
  "#065f46",
  "#064e3b",
];

const error: MantineColorsTuple = [
  "#fef2f2",
  "#fee2e2",
  "#fecaca",
  "#fca5a5",
  "#f87171", // Sell / negative
  "#ef4444",
  "#dc2626",
  "#b91c1c",
  "#991b1b",
  "#7f1d1d",
];

const warning: MantineColorsTuple = [
  "#fffbeb",
  "#fef3c7",
  "#fde68a",
  "#fcd34d",
  "#fbbf24",
  "#f59e0b",
  "#d97706",
  "#b45309",
  "#92400e",
  "#78350f",
];

const gray: MantineColorsTuple = [
  "#f0f3f7",
  "#e8edf3",
  "#e4eaf1",
  "#c9d4e0",
  "#aab8c9",
  "#7a90a8",
  "#5a7090",
  "#42546e",
  "#2d3f55",
  "#1a2332",
];

// Dark-mode component internals on the terminal surface family.
const dark: MantineColorsTuple = [
  "#e2e8f2",
  "#aebccd",
  "#7c97b5",
  "#42546e",
  "#363b44",
  "#2e333c",
  "#1e2229",
  "#191c23",
  "#191c23",
  "#15181e",
];

// Surface ramp light->dark; bg-7/8/9 are the card/sidebar/page dark surfaces.
const bg: MantineColorsTuple = [
  "#f0f3f7",
  "#e4eaf1",
  "#c9d4e0",
  "#8fa3ba",
  "#42546e",
  "#363b44",
  "#2e333c",
  "#1e2229",
  "#191c23",
  "#15181e",
];

const CARD = "var(--pulse-card, var(--mantine-color-body))";
const HAIRLINE = "var(--pulse-border, var(--mantine-color-default-border))";
const MUTED_BG = "var(--pulse-muted, var(--mantine-color-default-hover))";
const MONO = "var(--mantine-font-family-monospace)";

const inputStyles = {
  label: {
    color: "var(--mantine-color-text)",
    fontSize: "10px",
    fontWeight: 500,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
  } as React.CSSProperties,
  input: {
    backgroundColor: MUTED_BG,
    borderColor: HAIRLINE,
    color: "var(--mantine-color-text)",
    fontFamily: MONO,
    "--input-bd-focus":
      "color-mix(in srgb, var(--pulse-primary, #8fa3ba) 40%, transparent)",
  } as React.CSSProperties,
};

const optionStyles = {
  option: {
    color: "var(--mantine-color-text)",
  } as React.CSSProperties,
};

export const pulseTheme = createTheme({
  primaryColor: "primary",
  primaryShade: { light: 8, dark: 2 },
  colors: {
    primary: slate,
    secondary: slate,
    tertiary: slate,
    bg,
    success,
    warning,
    error,
    gray,
    dark,
  },
  fontFamily: "Inter, system-ui, -apple-system, sans-serif",
  fontFamilyMonospace:
    "'JetBrains Mono', ui-monospace, 'SF Mono', Menlo, monospace",
  headings: {
    fontFamily: "Inter, system-ui, -apple-system, sans-serif",
    fontWeight: "600",
  },
  fontSizes: {
    xs: "11px",
    sm: "12px",
    md: "13px",
    lg: "15px",
    xl: "18px",
  },
  radius: {
    xs: "2px",
    sm: "4px",
    md: "6px",
    lg: "8px",
    xl: "10px",
  },
  defaultRadius: "md",
  spacing: {
    xs: "4px",
    sm: "8px",
    md: "16px",
    lg: "24px",
    xl: "32px",
  },
  // Elevation is surface color only — no shadow depth anywhere.
  shadows: {
    xs: "none",
    sm: "none",
    md: "none",
    lg: "none",
    xl: "none",
  },
  components: {
    Modal: {
      defaultProps: {
        centered: true,
        radius: "md",
        padding: 20,
        overlayProps: {
          backgroundOpacity: 0.5,
          blur: 0,
          color: "#000000",
        },
        styles: {
          content: {
            background: CARD,
            border: `1px solid ${HAIRLINE}`,
            boxShadow: "none",
          } as React.CSSProperties,
          header: {
            background: "transparent",
          } as React.CSSProperties,
        },
      },
    },
    Drawer: {
      defaultProps: {
        overlayProps: {
          backgroundOpacity: 0.5,
          blur: 0,
          color: "#000000",
        },
        styles: {
          content: {
            background: CARD,
            boxShadow: "none",
          } as React.CSSProperties,
          header: {
            background: "transparent",
          } as React.CSSProperties,
        },
      },
    },
    Button: {
      defaultProps: {
        variant: "default",
        styles: {
          root: {
            transition: "background-color 0.12s ease, color 0.12s ease",
          } as React.CSSProperties,
        },
      },
      // primaryShade { dark: 2 } would resolve success/error to index 2 (washed-out tints).
      // Force the semantic shades (index 4) directly via CSS vars.
      vars: (_theme: MantineTheme, props: { variant?: string; color?: string }) => {
        if (props.variant === "filled") {
          if (props.color === "success") {
            return {
              root: {
                "--button-bg": "light-dark(var(--mantine-color-success-4), var(--mantine-color-success-6))",
                "--button-hover": "light-dark(var(--mantine-color-success-5), var(--mantine-color-success-7))",
              },
            };
          }
          if (props.color === "error") {
            return {
              root: {
                "--button-bg": "light-dark(var(--mantine-color-error-4), var(--mantine-color-error-6))",
                "--button-hover": "light-dark(var(--mantine-color-error-5), var(--mantine-color-error-7))",
              },
            };
          }
          if (props.color === "blue") {
            return {
              root: {
                "--button-bg": "light-dark(var(--mantine-color-blue-5), var(--mantine-color-blue-6))",
                "--button-hover": "light-dark(var(--mantine-color-blue-6), var(--mantine-color-blue-7))",
              },
            };
          }
        }
        return { root: {} };
      },
    },
    Badge: {
      defaultProps: {
        variant: "light",
        radius: "sm",
        styles: {
          root: {
            border:
              "1px solid color-mix(in srgb, currentColor 25%, transparent)",
            fontFamily: MONO,
            fontSize: 10,
            fontWeight: 500,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
          } as React.CSSProperties,
        },
      },
    },
    SegmentedControl: {
      defaultProps: {
        styles: {
          root: {
            background: MUTED_BG,
          } as React.CSSProperties,
          indicator: {
            border: `1px solid var(--pulse-active-border, ${HAIRLINE})`,
            boxShadow: "none",
          } as React.CSSProperties,
        },
      },
      // Same primaryShade issue as Button — override --sc-color directly.
      vars: (_theme: MantineTheme, props: { color?: string }) => {
        if (props.color === "success") {
          return { root: { "--sc-color": "light-dark(var(--mantine-color-success-4), var(--mantine-color-success-6))" } };
        }
        if (props.color === "error") {
          return { root: { "--sc-color": "light-dark(var(--mantine-color-error-4), var(--mantine-color-error-6))" } };
        }
        if (props.color === "blue") {
          return { root: { "--sc-color": "light-dark(var(--mantine-color-blue-5), var(--mantine-color-blue-6))" } };
        }
        return { root: { "--sc-color": "var(--pulse-active-bg, var(--mantine-color-default))" } };
      },
    },
    Select: {
      defaultProps: {
        styles: { ...inputStyles, ...optionStyles },
      },
    },
    Autocomplete: {
      defaultProps: {
        styles: { ...inputStyles, ...optionStyles },
      },
    },
    NumberInput: {
      defaultProps: {
        // Only "." is a decimal separator so a pasted "1,234.53" keeps its value;
        // Mantine's default [".", ","] rewrites the comma to a second dot on paste.
        allowedDecimalSeparators: ["."],
        styles: inputStyles,
      },
    },
    TextInput: {
      defaultProps: {
        styles: inputStyles,
      },
    },
    Textarea: {
      defaultProps: {
        styles: inputStyles,
      },
    },
    TimeInput: {
      defaultProps: {
        styles: inputStyles,
      },
    },
    DatePickerInput: {
      defaultProps: {
        styles: inputStyles,
      },
    },
    DateTimePicker: {
      defaultProps: {
        styles: inputStyles,
      },
    },
    NativeSelect: {
      defaultProps: {
        styles: inputStyles,
      },
    },
    Checkbox: {
      defaultProps: {
        color: "primary",
        styles: {
          label: { color: "var(--mantine-color-text)" } as React.CSSProperties,
        },
      },
    },
    Radio: {
      defaultProps: {
        styles: {
          label: { color: "var(--mantine-color-text)" } as React.CSSProperties,
        },
      },
    },
    Switch: {
      defaultProps: {
        styles: {
          label: { color: "var(--mantine-color-text)" } as React.CSSProperties,
        },
      },
    },
  },
});
